/**
 * CREATIVE DETONATOR - Phase 1: Ignition
 * Brief input functionality (product, benefit, format selection)
 *
 * This phase collects:
 * - Product name
 * - Key benefit
 * - Output format(s) - supports multi-select
 */

// v63 - Added product picker when brand with products is loaded
import { GeneratorState, elements, hasBrand } from '../core/state.js';
import { goToPhase } from '../core/navigation.js';
import { debounce } from '../../../js/core/timing.js';
import { api } from '../../../js/api/client.js';
import { addFormat, removeFormat, updateBrief } from '../../../js/store/actions/generator.js';

// Access DataStore from global scope (defined in main.js)
const DataStore = window.DataStore;

// Track loaded brand products
let brandProducts = [];

// Debounced summary update for input handlers
const debouncedUpdateSummary = debounce(updateBriefSummary, 150);

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize Phase 1 (Ignition)
 * Sets up all event listeners for brief input
 */
export async function initPhase1() {
  // Dev navigation buttons (jump between questions) - handle ALL nav buttons across all navs
  document.querySelectorAll('.dev-nav-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const questionId = btn.dataset.question;
      goToQuestion(questionId);
    });
  });

  // V5: Pre-fill product input if brand is loaded
  if (hasBrand() && GeneratorState.currentBrand) {
    const brand = GeneratorState.currentBrand;
    elements.inputProduct.value = brand.name;
    updateBrief({ product: brand.name });
    showBrandHelp(brand);
    updateNextButton('q1', 'q2', true);
    debouncedUpdateSummary();

    // Load brand products for product picker
    await loadBrandProducts(brand.id);
  }

  // Product input (debounced summary, immediate button state)
  elements.inputProduct.addEventListener('input', (e) => {
    const product = e.target.value.trim();
    updateBrief({ product });
    debouncedUpdateSummary();
    updateNextButton('q1', 'q2', product.length > 0);
    // V5: Hide brand help if user edits input to be different from brand name
    if (hasBrand() && GeneratorState.currentBrand) {
      if (e.target.value.trim() !== GeneratorState.currentBrand.name) {
        hideBrandHelp();
      } else {
        showBrandHelp(GeneratorState.currentBrand);
      }
    }
  });

  elements.inputProduct.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' && GeneratorState.brief.product) {
      goToQuestion('q2');
    }
  });

  // Benefit input (debounced summary, immediate button state)
  elements.inputBenefit.addEventListener('input', (e) => {
    const benefit = e.target.value.trim();
    updateBrief({ benefit });
    debouncedUpdateSummary();
    updateNextButton('q2', 'q3', benefit.length > 0);
  });

  elements.inputBenefit.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' && GeneratorState.brief.benefit) {
      goToQuestion('q3');
    }
  });

  // Format selection - MULTI-SELECT toggle behavior
  elements.formatCards.forEach(card => {
    card.addEventListener('click', () => {
      const format = card.dataset.format;
      console.log('[Phase1] Format clicked:', format);
      console.log('[Phase1] Store available:', !!window.Store);

      if (card.classList.contains('selected')) {
        // DESELECT: Remove from Store
        card.classList.remove('selected');
        removeFormat(format);
        console.log('[Phase1] Removed format:', format);
      } else {
        // SELECT: Add to Store
        card.classList.add('selected');
        addFormat(format);
        console.log('[Phase1] Added format:', format);
      }

      const formats = GeneratorState.brief.formats;
      console.log('[Phase1] Current formats:', formats);
      console.log('[Phase1] Formats length:', formats.length);

      updateBriefSummary();

      // Enable button if at least 1 format selected
      elements.btnToArming.disabled = formats.length === 0;
      console.log('[Phase1] Button disabled:', elements.btnToArming.disabled);
    });
  });

  // Next buttons
  document.querySelectorAll('.btn-next').forEach(btn => {
    btn.addEventListener('click', () => {
      const nextQuestion = btn.dataset.next;
      goToQuestion(nextQuestion);
    });
  });

  // To Arming button
  elements.btnToArming.addEventListener('click', () => {
    goToPhase(2);
  });
}

// ============================================================================
// QUESTION NAVIGATION
// ============================================================================

/**
 * Navigate to a specific question screen
 * @param {string} questionId - The ID of the question to show (q1, q2, q3)
 */
function goToQuestion(questionId) {
  elements.questionScreens.forEach(screen => screen.classList.remove('active'));
  document.getElementById(questionId).classList.add('active');

  // Update ALL dev nav buttons across all navs
  document.querySelectorAll('.dev-nav-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.question === questionId);
  });
}

/**
 * Update the enabled state of a next button
 * @param {string} currentQ - Current question ID
 * @param {string} nextQ - Next question ID
 * @param {boolean} isValid - Whether the current input is valid
 */
function updateNextButton(currentQ, nextQ, isValid) {
  const btn = document.querySelector(`#${currentQ} .btn-next`);
  if (btn) {
    btn.disabled = !isValid;
  }
}

// ============================================================================
// BRIEF SUMMARY
// ============================================================================

/**
 * Update the brief summary display in navigation
 * Also triggers credit counter update
 */
export function updateBriefSummary() {
  const { product, benefit, formats, productId } = GeneratorState.brief;

  // Check if using a brand product (has productId set)
  const selectedProduct = productId ? brandProducts.find(p => p.id === productId) : null;
  const productDisplay = selectedProduct
    ? `${product} ★`  // Star indicates brand product selected
    : product || '';

  // Update all nav values (multiple navs exist, one per question screen)
  document.querySelectorAll('#nav-product-value, .nav-product-value').forEach(el => {
    el.textContent = productDisplay;
    // Add tooltip for brand products
    if (selectedProduct) {
      el.title = `Brand product: ${selectedProduct.name}${selectedProduct.description ? `\n${selectedProduct.description}` : ''}`;
    } else {
      el.title = '';
    }
  });
  document.querySelectorAll('#nav-benefit-value, .nav-benefit-value').forEach(el => {
    el.textContent = benefit || '';
  });
  document.querySelectorAll('#nav-format-value, .nav-format-value').forEach(el => {
    if (formats.length > 0) {
      el.textContent = formats.length === 1
        ? formats[0].toUpperCase()
        : `${formats.length} FORMATS`;
    } else {
      el.textContent = '';
    }
  });

  // Log when brand product is being used
  if (selectedProduct) {
    console.log(`[Phase1] Using brand product in brief: ${selectedProduct.name} (ID: ${productId})`);
  }

  // Update credit counter (defined in phase2 but may be called before phase 2 loads)
  if (typeof window.updateCreditCounter === 'function') {
    window.updateCreditCounter();
  }
}

// ============================================================================
// BRAND HELP (V5)
// ============================================================================

/**
 * Show brand help message below product input
 * @param {Object} brand - The loaded brand object
 */
function showBrandHelp(brand) {
  const container = document.getElementById('q1');
  if (!container) return;

  // Remove existing help if any
  hideBrandHelp();

  // Create help element
  const helpEl = document.createElement('p');
  helpEl.className = 'brand-help';
  helpEl.id = 'brand-help-text';
  helpEl.innerHTML = `
    <span class="brand-loaded-icon">+</span>
    Pre-filled from <strong>${brand.name}</strong> cartridge.
    You can edit this to specify a product (e.g., "${brand.name} Pro").
  `;

  // Insert after the input
  const inputWrapper = container.querySelector('.question-content');
  if (inputWrapper) {
    inputWrapper.appendChild(helpEl);
  }
}

/**
 * Hide brand help message
 */
function hideBrandHelp() {
  const helpEl = document.getElementById('brand-help-text');
  if (helpEl) {
    helpEl.remove();
  }
}

// ============================================================================
// PRODUCT PICKER (V5)
// ============================================================================

/**
 * Load products for the current brand
 * @param {string} brandId - The brand UUID
 */
async function loadBrandProducts(brandId) {
  try {
    const response = await api.getProducts(brandId);
    if (response.success && response.products?.length > 0) {
      brandProducts = response.products;
      console.log(`[Phase1] Loaded ${brandProducts.length} products for brand`);
      renderProductPicker();

      // Auto-select first product for better UX
      if (brandProducts.length > 0) {
        autoSelectFirstProduct();
      }
    } else {
      console.log('[Phase1] No products found for this brand');
      brandProducts = [];
    }
  } catch (error) {
    console.warn('[Phase1] Failed to load brand products:', error);
    brandProducts = [];
  }
}

/**
 * Auto-select the first product when brand has products
 * This ensures product data is used in generation by default
 */
function autoSelectFirstProduct() {
  const firstProduct = brandProducts[0];
  if (!firstProduct) return;

  console.log(`[Phase1] Auto-selecting product: ${firstProduct.name}`);
  console.log(`[Phase1] Auto-select product description:`, firstProduct.description || 'none');

  // Set product ID and data for generation via Store action
  updateBrief({ productId: firstProduct.id, productData: firstProduct });

  // Store full product object for image prompts and modal context bar
  GeneratorState.selectedProduct = firstProduct;
  console.log(`[Phase1] GeneratorState.selectedProduct set:`, GeneratorState.selectedProduct?.name || 'null');
  console.log(`[Phase1] GeneratorState.brief.productData set:`, GeneratorState.brief.productData?.description?.substring(0, 30) || 'null');

  // Fill inputs
  if (elements.inputProduct) {
    elements.inputProduct.value = firstProduct.name;
    updateBrief({ product: firstProduct.name });
    updateNextButton('q1', 'q2', true);
  }

  if (firstProduct.default_benefit && elements.inputBenefit) {
    elements.inputBenefit.value = firstProduct.default_benefit;
    updateBrief({ benefit: firstProduct.default_benefit });
    updateNextButton('q2', 'q3', true);
  }

  // Update the dropdown to show selection
  setTimeout(() => {
    const select = document.getElementById('product-picker-select');
    if (select) {
      select.value = firstProduct.id;
    }
    // Show product info
    showProductInfo(firstProduct);
    debouncedUpdateSummary();
  }, 100);
}

/**
 * Render the product picker dropdown
 */
function renderProductPicker() {
  if (brandProducts.length === 0) return;

  const container = document.getElementById('q1');
  if (!container) return;

  // Remove existing picker if any
  removeProductPicker();

  // Create picker element with enhanced UI
  const pickerEl = document.createElement('div');
  pickerEl.className = 'product-picker';
  pickerEl.id = 'product-picker';
  pickerEl.innerHTML = `
    <div class="product-picker-header">
      <span class="product-picker-icon">▼</span>
      <label class="product-picker-label">BRAND PRODUCTS (${brandProducts.length})</label>
    </div>
    <select class="product-picker-select" id="product-picker-select">
      <option value="">-- Select a product or type custom above --</option>
      ${brandProducts.map(p => `
        <option value="${p.id}"
                data-name="${escapeAttr(p.name)}"
                data-benefit="${escapeAttr(p.default_benefit || '')}"
                data-description="${escapeAttr(p.description || '')}">
          ${escapeHtml(p.name)}
        </option>
      `).join('')}
    </select>
    <div class="product-info" id="product-info" style="display: none;">
      <div class="product-info-description" id="product-info-description"></div>
    </div>
  `;

  // Insert after brand help (if exists) or after input
  const brandHelp = document.getElementById('brand-help-text');
  const inputWrapper = container.querySelector('.question-content');
  if (brandHelp) {
    brandHelp.insertAdjacentElement('afterend', pickerEl);
  } else if (inputWrapper) {
    inputWrapper.appendChild(pickerEl);
  }

  // Bind select event
  const select = document.getElementById('product-picker-select');
  select?.addEventListener('change', handleProductSelection);
}

/**
 * Show product info panel with description
 * @param {Object} product - Product object
 */
function showProductInfo(product) {
  const infoEl = document.getElementById('product-info');
  const descEl = document.getElementById('product-info-description');

  if (!infoEl || !descEl) return;

  if (product && product.description) {
    descEl.innerHTML = `
      <strong>Description:</strong> ${escapeHtml(product.description)}
      ${product.default_benefit ? `<br><strong>Benefit:</strong> ${escapeHtml(product.default_benefit)}` : ''}
    `;
    infoEl.style.display = 'block';
    console.log(`[Phase1] Product description: ${product.description.substring(0, 50)}...`);
  } else {
    infoEl.style.display = 'none';
  }
}

/**
 * Hide product info panel
 */
function hideProductInfo() {
  const infoEl = document.getElementById('product-info');
  if (infoEl) {
    infoEl.style.display = 'none';
  }
}

/**
 * Remove the product picker
 */
function removeProductPicker() {
  const picker = document.getElementById('product-picker');
  if (picker) {
    picker.remove();
  }
}

/**
 * Handle product selection from dropdown
 * @param {Event} e - Change event
 */
function handleProductSelection(e) {
  const select = e.target;
  const selectedOption = select.options[select.selectedIndex];

  if (!selectedOption || !selectedOption.value) {
    // Reset if "No product" selected - clear everything
    updateBrief({ productId: null, productData: null, product: '' });
    GeneratorState.selectedProduct = null;  // Clear selected product

    // Clear the product input field
    if (elements.inputProduct) {
      elements.inputProduct.value = '';
      updateNextButton('q1', 'q2', false);  // Disable next since product is now empty
    }

    // Clear benefit too since it was from the product
    if (elements.inputBenefit) {
      elements.inputBenefit.value = '';
      updateBrief({ benefit: '' });
      updateNextButton('q2', 'q3', false);
    }

    hideProductInfo();
    debouncedUpdateSummary();
    console.log('[Phase1] Product deselected - cleared all product fields');
    return;
  }

  const productId = selectedOption.value;
  const productName = selectedOption.dataset.name;
  const productBenefit = selectedOption.dataset.benefit;
  const productDescription = selectedOption.dataset.description;

  // Find full product object for info display
  const product = brandProducts.find(p => p.id === productId);

  console.log(`[Phase1] Product selected: ${productName} (ID: ${productId})`);
  console.log(`[Phase1] Product object:`, product);
  console.log(`[Phase1] Product description:`, product?.description || 'none');

  // Set product ID and data for generation via Store action
  updateBrief({ productId: productId, productData: product || null });

  // Store full product object for image prompts and modal context bar
  GeneratorState.selectedProduct = product || null;
  console.log(`[Phase1] GeneratorState.selectedProduct set:`, GeneratorState.selectedProduct?.name || 'null');
  console.log(`[Phase1] GeneratorState.brief.productData set:`, GeneratorState.brief.productData?.description?.substring(0, 30) || 'null');

  // Pre-fill product name
  if (productName && elements.inputProduct) {
    elements.inputProduct.value = productName;
    updateBrief({ product: productName });
    updateNextButton('q1', 'q2', true);
  }

  // Pre-fill benefit if available
  if (productBenefit && elements.inputBenefit) {
    elements.inputBenefit.value = productBenefit;
    updateBrief({ benefit: productBenefit });
    updateNextButton('q2', 'q3', true);
  }

  // Show product info panel
  if (product) {
    showProductInfo(product);
  }

  debouncedUpdateSummary();
}

/**
 * Escape HTML entities
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Escape attribute value
 */
function escapeAttr(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// ============================================================================
// REFRESH PRODUCTS (called from modal when brand changes)
// ============================================================================

/**
 * Refresh the product picker for a new brand
 * Called from generator.js when user selects a different brand from modal
 * @param {string} brandId - The new brand's UUID
 * @param {string|null} selectedProductId - Product ID if one was selected, null for "no product"
 */
async function refreshProductsForBrand(brandId, selectedProductId = null) {
  if (!brandId) {
    // No brand - remove product picker
    removeProductPicker();
    brandProducts = [];
    return;
  }

  try {
    const response = await api.getProducts(brandId);
    if (response.success && response.products?.length > 0) {
      brandProducts = response.products;
      console.log(`[Phase1] Refreshed products for brand: ${brandProducts.length} products`);
      renderProductPicker();

      // Set dropdown to match the selected product (or "no product" if null)
      const select = document.getElementById('product-picker-select');
      if (select) {
        select.value = selectedProductId || '';
      }

      // Show/hide product info based on selection
      if (selectedProductId) {
        const product = brandProducts.find(p => p.id === selectedProductId);
        if (product) {
          showProductInfo(product);
        }
      } else {
        hideProductInfo();
      }
    } else {
      console.log('[Phase1] No products found for this brand');
      removeProductPicker();
      brandProducts = [];
    }
  } catch (error) {
    console.warn('[Phase1] Failed to refresh brand products:', error);
    removeProductPicker();
    brandProducts = [];
  }
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility
window.initPhase1 = initPhase1;
window.updateBriefSummary = updateBriefSummary;
window.refreshProductsForBrand = refreshProductsForBrand;
